import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './IndustriesPage.css';
import { ROUTES } from '../../utils/routes';

const industriesBanner = process.env.PUBLIC_URL + '/images/industries/public-sector.jpg';

export default function IndustriesPage() {
  const [industriesData, setIndustriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(0);

  const contentSectionRefs = useRef([]);
  const sidebarRef = useRef(null);
  const isClickScrolling = useRef(false);
  const clickScrollTimer = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/industry-page`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-username': `${process.env.REACT_APP_API_URL_USERNAME}`,
        'api-password': `${process.env.REACT_APP_API_URL_PASSWORD}`,
      },
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .then((data) => setIndustriesData(data.industries))
      .catch((err) => console.log(err))
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleNavigateHome = () => {
    navigate(ROUTES.HOME);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExpertCTA = () => {
    navigate(ROUTES.CONTACT);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll detection on window — fires whenever user scrolls, but skip when
  // we triggered the scroll ourselves via a sidebar click.
  const handleScroll = useCallback(() => {
    if (isClickScrolling.current) return;

    const OFFSET = 200; // px from top at which a section is considered "active"
    let current = 0;

    contentSectionRefs.current.forEach((ref, index) => {
      if (ref) {
        const rect = ref.getBoundingClientRect();
        if (rect.top <= OFFSET) {
          current = index;
        }
      }
    });

    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleSectionClick = (index) => {
    setActiveSection(index);

    const element = contentSectionRefs.current[index];
    if (!element) return;

    // Suppress scroll-listener updates while the smooth-scroll animation runs
    isClickScrolling.current = true;
    clearTimeout(clickScrollTimer.current);

    const top = element.getBoundingClientRect().top + window.pageYOffset - 120;
    window.scrollTo({ top, behavior: 'smooth' });

    // Re-enable scroll detection after animation (~700 ms is plenty)
    clickScrollTimer.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, 800);
  };

  // Clean up timer on unmount
  useEffect(() => () => clearTimeout(clickScrollTimer.current), []);
  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }
  return (
    <div className="industries-page">
      {/* Inner Banner */}
      <section
        className="inner-page-banner"
        style={{ backgroundImage: `url(${industriesBanner})` }}
      >
        <div className="inner-page-banner-overlay" />
        <div className="container inner-page-banner-inner">
          <h1 className="inner-page-banner-title">Industries</h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="inner-page-breadcrumb-wrap">
        <div className="container">
          <nav className="inner-page-breadcrumb">
            <button
              type="button"
              className="inner-page-home"
              onClick={handleNavigateHome}
            >
              Home
            </button>
            <span className="inner-page-sep">
              <i className="bi bi-chevron-right" />
            </span>
            <span className="inner-page-current">Industries</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="industries-hero-section">
        <div className="container">
          <div className="industries-hero-content">
            <h1 className="industries-hero-title">
              Industry experience that connects strategy to execution
            </h1>
            <p className="industries-hero-intro">
              We tailor SAP, data, AI, and integration services to the operating
              realities, controls, and growth priorities of each industry we
              support.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="industries-detail-section">
        <div className="container">
          <div className="industries-wrapper-sidebar">

            {/* Sidebar */}
            <aside className="industries-sidebar" ref={sidebarRef}>
              <div className="industries-sidebar-sticky">
                <div className="industries-sidebar-sections">
                  <h2 className="sidebar-sections-title">Industries</h2>
                  <nav className="sidebar-nav">
                    {industriesData.map((industry, index) => (
                      <button
                        key={index}
                        className={`sidebar-nav-item ${activeSection === index ? 'active' : ''
                          }`}
                        onClick={() => handleSectionClick(index)}
                      >
                        {industry.name}
                        <span className="sidebar-nav-indicator" />
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>

            {/* Right Content */}
            <div className="industries-content-wrapper">
              <div className="industries-sections-container">
                {industriesData.map((industry, index) => (
                  <section
                    key={index}
                    className="industries-content-section"
                    ref={(el) => (contentSectionRefs.current[index] = el)}
                  >
                    <div className="numbered-section">
                      <h2 className="numbered-section-title">{industry.name}</h2>
                    </div>
                    <div
                      dangerouslySetInnerHTML={{ __html: industry.description }}
                    />
                  </section>
                ))}
              </div>

              {/* Case Studies */}
              <section
                className="industries-case-studies"
                style={{ marginTop: '60px' }}
              >
                <div className="section-header">
                  <h2 className="section-title">Case Study Highlights</h2>
                </div>
                <div className="case-studies-intro">
                  <div>
                    <h3>Public Sector</h3>
                    <p>
                      Supported a public sector organization with finance
                      modernization, governance improvements, and reporting
                      transformation.
                    </p>
                  </div>
                  <div>
                    <h3>Financial Services</h3>
                    <p>
                      Enhanced compliance reporting and risk visibility through
                      integrated data and reporting solutions.
                    </p>
                  </div>
                  <div>
                    <h3>Manufacturing</h3>
                    <p>
                      Improved ERP processes and enterprise reporting to
                      strengthen operational performance and resilience.
                    </p>
                  </div>
                </div>
              </section>

              {/* CTA */}
              <section className="industries-cta-section">
                <div className="cta-content">
                  <h2 className="cta-title">
                    Ready to discuss your industry challenges?
                  </h2>
                  <button className="cta-button" onClick={handleExpertCTA}>
                    Talk to an Expert
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}